import React, { useState } from 'react';
import { Card, Button, EmptyState, Modal, FormInput, FormSelect } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

type ChecklistCategory = 'review' | 'risk' | 'compliance' | 'testing' | 'documentation' | 'other';

interface ChecklistItem {
    id: number;
    strategyId: number;
    templateId: number | null;
    name: string;
    description: string | null;
    category: ChecklistCategory;
    isCompleted: number;
    completedAt: string | null;
    completedBy: number | null;
    completedByEmail: string | null;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
}

interface Props {
    strategyId: number;
    items: ChecklistItem[];
}

const categoryLabels: Record<ChecklistCategory, string> = {
    review: 'Review',
    risk: 'Risk',
    compliance: 'Compliance',
    testing: 'Testing',
    documentation: 'Documentation',
    other: 'Other'
};

const categoryOptions = Object.entries(categoryLabels).map(([value, label]) => ({
    value,
    label
}));

const ChecklistSection: React.FC<Props> = ({ strategyId, items }) => {
    const queryClient = useQueryClient();
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
    const [newItemName, setNewItemName] = useState('');
    const [newItemDescription, setNewItemDescription] = useState('');
    const [newItemCategory, setNewItemCategory] = useState<ChecklistCategory>('other');
    const [editItemName, setEditItemName] = useState('');
    const [editItemDescription, setEditItemDescription] = useState('');
    const [editItemCategory, setEditItemCategory] = useState<ChecklistCategory>('other');

    // Toggle mutation
    const toggleMutation = useTrpcMutation(
        trpc.strategyReadiness.toggleChecklistItem.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });
            }
        }),
        { fallbackErrorMessage: 'Failed to toggle item' }
    );

    // Add item mutation
    const addMutation = useTrpcMutation(
        trpc.strategyReadiness.addChecklistItem.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });

                setShowAddModal(false);
                setNewItemName('');
                setNewItemDescription('');
                setNewItemCategory('other');
            }
        }),
        { successMessage: 'Item added', fallbackErrorMessage: 'Failed to add item' }
    );

    // Delete mutation
    const deleteMutation = useTrpcMutation(
        trpc.strategyReadiness.deleteChecklistItem.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });
            }
        }),
        { successMessage: 'Item deleted', fallbackErrorMessage: 'Failed to delete item' }
    );

    // Update item mutation
    const updateMutation = useTrpcMutation(
        trpc.strategyReadiness.updateChecklistItem.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });

                setShowEditModal(false);
                setEditingItem(null);
                setEditItemName('');
                setEditItemDescription('');
                setEditItemCategory('other');
            }
        }),
        { successMessage: 'Item updated', fallbackErrorMessage: 'Failed to update item' }
    );

    // Initialize from templates mutation
    const initializeMutation = useTrpcMutation(
        trpc.strategyReadiness.initializeChecklist.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });
            }
        }),
        { successMessage: 'Checklist initialized from templates', fallbackErrorMessage: 'Failed to initialize checklist' }
    );

    const handleToggle = async (item: ChecklistItem) => {
        await toggleMutation.mutateAsync({
            id: item.id,
            isCompleted: !item.isCompleted
        });
    };

    const handleAddItem = async () => {
        if (!newItemName.trim()) return;

        await addMutation.mutateAsync({
            strategyId,
            name: newItemName.trim(),
            description: newItemDescription.trim() || undefined,
            category: newItemCategory
        });
    };

    const handleDelete = async (itemId: number) => {
        if (!confirm('Delete this checklist item?')) return;
        await deleteMutation.mutateAsync({ id: itemId });
    };

    const handleEditItem = (item: ChecklistItem) => {
        setEditingItem(item);
        setEditItemName(item.name);
        setEditItemDescription(item.description ?? '');
        setEditItemCategory(item.category);
        setShowEditModal(true);
    };

    const handleUpdateItem = async () => {
        if (!editingItem || !editItemName.trim()) return;

        await updateMutation.mutateAsync({
            id: editingItem.id,
            name: editItemName.trim(),
            description: editItemDescription.trim() ? editItemDescription.trim() : null,
            category: editItemCategory
        });
    };

    const completedCount = items.filter((i) => i.isCompleted).length;

    return (
        <>
            <Card
                headerActions={
                    <div className="flex gap-2">
                        {items.length === 0 && (
                            <Button
                                isLoading={initializeMutation.isPending}
                                size="sm"
                                variant="outline-dark"
                                onClick={() => initializeMutation.mutate({ strategyId })}
                            >
                                Initialize from Templates
                            </Button>
                        )}
                        <Button size="sm" variant="primary" onClick={() => setShowAddModal(true)}>
                            Add Item
                        </Button>
                    </div>
                }
                subtitle={`${completedCount} of ${items.length} completed`}
                title="Checklist"
            >
                {items.length === 0 ? (
                    <EmptyState description="Add items manually or initialize from your templates." title="No checklist items yet." />
                ) : (
                    <div className="space-y-2">
                        {items.map((item) => (
                            <div
                                className={`p-3 rounded ${item.isCompleted ? 'bg-green-900/10' : 'bg-zinc-800'} flex items-start gap-3`}
                                key={item.id}
                            >
                                <input
                                    checked={!!item.isCompleted}
                                    className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-green-500 focus:ring-green-500 focus:ring-offset-zinc-900 cursor-pointer"
                                    disabled={toggleMutation.isPending}
                                    type="checkbox"
                                    onChange={() => handleToggle(item)}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`font-medium ${item.isCompleted ? 'line-through text-zinc-500' : 'text-zinc-200'}`}
                                        >
                                            {item.name}
                                        </span>
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400">
                                            {categoryLabels[item.category]}
                                        </span>
                                    </div>
                                    {item.description && <p className="text-sm text-zinc-400 mt-0.5">{item.description}</p>}
                                    <p className="text-xs text-zinc-500 mt-1">Added {dayjs(item.createdAt).fromNow()}</p>
                                    {!!item.isCompleted && item.completedAt && (
                                        <p className="text-xs text-zinc-500 mt-1">
                                            Completed {dayjs(item.completedAt).format('MMM D, YYYY h:mm A')}
                                            {item.completedByEmail && ` by ${item.completedByEmail}`}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
                                        disabled={updateMutation.isPending}
                                        title="Edit item"
                                        onClick={() => handleEditItem(item)}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                                        disabled={deleteMutation.isPending}
                                        title="Delete item"
                                        onClick={() => handleDelete(item.id)}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Add Item Modal */}
            <Modal
                footer={
                    <>
                        <Button variant="outline-dark" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </Button>
                        <Button disabled={!newItemName.trim()} isLoading={addMutation.isPending} variant="primary" onClick={handleAddItem}>
                            Add Item
                        </Button>
                    </>
                }
                isOpen={showAddModal}
                title="Add Checklist Item"
                onClose={() => setShowAddModal(false)}
            >
                <div className="space-y-4">
                    <FormInput
                        id="itemName"
                        label="Name"
                        placeholder="Enter item name"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                    />
                    <FormInput
                        id="itemDescription"
                        label="Description (optional)"
                        placeholder="Enter description"
                        value={newItemDescription}
                        onChange={(e) => setNewItemDescription(e.target.value)}
                    />
                    <FormSelect
                        id="itemCategory"
                        label="Category"
                        options={categoryOptions}
                        value={newItemCategory}
                        onChange={(e) => setNewItemCategory(e.target.value as ChecklistCategory)}
                    />
                </div>
            </Modal>

            {/* Edit Item Modal */}
            <Modal
                footer={
                    <>
                        <Button
                            variant="outline-dark"
                            onClick={() => {
                                setShowEditModal(false);
                                setEditingItem(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!editItemName.trim()}
                            isLoading={updateMutation.isPending}
                            variant="primary"
                            onClick={handleUpdateItem}
                        >
                            Save Changes
                        </Button>
                    </>
                }
                isOpen={showEditModal}
                title="Edit Checklist Item"
                onClose={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                }}
            >
                <div className="space-y-4">
                    <FormInput
                        id="editItemName"
                        label="Name"
                        placeholder="Enter item name"
                        value={editItemName}
                        onChange={(e) => setEditItemName(e.target.value)}
                    />
                    <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-1" htmlFor="editItemDescription">
                            Description / note (optional)
                        </label>
                        <textarea
                            className="w-full bg-zinc-800 border border-zinc-700 rounded p-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            id="editItemDescription"
                            placeholder="Add a note..."
                            rows={3}
                            value={editItemDescription}
                            onChange={(e) => setEditItemDescription(e.target.value)}
                        />
                    </div>
                    <FormSelect
                        id="editItemCategory"
                        label="Category"
                        options={categoryOptions}
                        value={editItemCategory}
                        onChange={(e) => setEditItemCategory(e.target.value as ChecklistCategory)}
                    />
                </div>
            </Modal>
        </>
    );
};

export default ChecklistSection;
